import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
} from "@mui/material";
import { Dispatch, SetStateAction, memo, useState } from "react";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import { INVALID_DATE_MESSAGE } from "@/components/filter/filter-constant";
import { isInvalidDate } from "@/utils/date-time-utility";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomSelectScroll from "../../../../../components/CustomSelectScroll";
import Flex from "../../../../../components/Flex";
import LabeledSwitch from "../../../../../components/LabeledSwitch";
import GlobalFilter from "../../../../../components/GlobalFilter";
import Input from "../../../../../components/Input";
import Text from "../../../../../components/Text";
import CustomTabs from "../../../../../components/app/CustomTabs";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { useProvidersList } from "../../../../../queries/provider/setting/use-providers-list";
import { useSpecialtiesList } from "../../../../../queries/use-specialities-list";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import {
  DEFAULT_FILTERS,
  REFERRAL_FILTER_INITIAL_EXPANDED,
  REFERRAL_FILTER_KEYS,
  REFERRAL_TABS,
  ReferralFilters,
} from "../collaboration-referral-constant";
import UpsertReferral from "./UpsertReferral";
import { useReduceDisclosure } from "../../../../../hooks/use-reduce-disclosure";
import NewReferringProvider from "./NewReferringProvider";
import { useActiveComponentCountController } from "../../../../../components/filter/use-ActiveComponentCountController";
import SearchInput from "../../../../../components/SearchInput";

interface CollaborationTopBarProps {
  activeReferral: string;
  setActiveReferral: (v: string) => void;
  setSearchParams: (v: { activeReferral: string }) => void;
  setFilters: Dispatch<SetStateAction<ReferralFilters>>;
  filters: ReferralFilters;
  setSearchStringKey?: (v: string) => void;
  searchStringKey?: string;
  setExpanded: Dispatch<SetStateAction<Record<string, boolean>>>;
  setArchive: Dispatch<SetStateAction<boolean>>;
  archive: boolean;
  expanded: Record<string, boolean>;
  setIsReferral_Provider_list: (v: boolean) => void;
  patientData: any;
}

const ReferralTopBar = ({
  activeReferral,
  setActiveReferral,
  setSearchParams,
  setFilters,
  filters,
  setSearchStringKey,
  searchStringKey,
  setExpanded,
  expanded,
  setIsReferral_Provider_list,
  setArchive,
  archive,
  patientData,
}: CollaborationTopBarProps) => {
  return (
    <Flex justifyContent={"space-between"} height={"2.5rem"}>
      <CustomTabs
        tabsData={
          !patientData
            ? REFERRAL_TABS
            : REFERRAL_TABS.filter(
                (tab) => tab.value !== "referral-provider-list"
              )
        }
        value={activeReferral}
        onChange={(v) => {
          setActiveReferral(v);
          setSearchParams({ activeReferral: v });

          // this is to clear the search string and filters on tab change
          setSearchStringKey?.("");
          setFilters(DEFAULT_FILTERS);

          // this is to set the referral provider list filter to true or false
          if (v === "referral-provider-list") {
            setIsReferral_Provider_list(true);
          } else {
            setIsReferral_Provider_list(false);
          }
        }}
      />
      <Flex gap={1}>
        <LabeledSwitch
          label={`Show Archived ${
            activeReferral === "referral-provider-list"
              ? "Referral Providers"
              : "Referrals"
          }`}
          checked={archive}
          onChange={(e) => setArchive(e.target.checked)}
        />
        <ReferralEndActions
          setFilters={setFilters}
          filters={filters}
          activeReferral={activeReferral}
          setSearchStringKey={setSearchStringKey}
          searchStringKey={searchStringKey}
          expanded={expanded}
          setExpanded={setExpanded}
          patientData={patientData}
        />
      </Flex>
    </Flex>
  );
};

export default ReferralTopBar;

interface EndActionsProps {
  setFilters: Dispatch<SetStateAction<ReferralFilters>>;
  filters: ReferralFilters;
  activeReferral: string;
  setSearchStringKey?: (v: string) => void;
  searchStringKey?: string;
  setExpanded: Dispatch<SetStateAction<Record<string, boolean>>>;
  expanded: Record<string, boolean>;
  showReferralInOutfilter?: boolean;
  referringOutFromEncounter?: boolean;
  prefillReferralOutData?: any;
  hideFilters?: boolean;
  patientData?: any;
}

export const ReferralEndActions = memo(
  ({
    setFilters,
    filters,
    activeReferral,
    setExpanded,
    expanded,
    setSearchStringKey,
    searchStringKey,
    showReferralInOutfilter = false,
    referringOutFromEncounter,
    prefillReferralOutData,
    hideFilters = false,
    patientData,
  }: EndActionsProps) => {
    const [referralDisclosureHost, newReferringProvider] =
      useReduceDisclosure(2);
    const [localFilters, setLocalFilters] = useState<ReferralFilters>(filters);
    // console.log(setSearchStringKey, searchStringKey);

    const activeFilterCount = useActiveComponentCountController(filters);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isFilterOpen = !!anchorEl;

    const [searchProviderKey, setSearchProviderKey] = useDebounce<string>("");

    const [providerPagination, setProviderPagination] =
      useState<DGT_Pagination>(DEF_PAGINATION);
    const { data: providersList, isFetching: providerFetching } =
      useProvidersList({
        pagination: providerPagination,
        searchKey: searchProviderKey,
        xTENANTID: getXtenantId(),
        enabled: activeReferral !== "referral-provider-list" && isFilterOpen,
      });

    const [referralProviderPagination, setReferralProviderPagination] =
      useState<DGT_Pagination>(DEF_PAGINATION);
    const {
      data: referralProvidersList,
      isFetching: referralProviderFetching,
    } = useProvidersList({
      pagination: referralProviderPagination,
      searchKey: searchProviderKey,
      isReferral: true,
      xTENANTID: getXtenantId(),
      enabled: activeReferral !== "referral-provider-list" && isFilterOpen,
    });

    const handleProviderSearch = (val: string) => {
      setSearchProviderKey(val);
      setProviderPagination((p) => ({ ...p, page: 0 }));
      setReferralProviderPagination((p) => ({ ...p, page: 0 }));
    };

    const [specialitySearch, setSpecialitySearch] = useDebounce<string>("");
    const [specialityPagination, setSpecialityPagination] =
      useState<DGT_Pagination>(DEF_PAGINATION);
    const { data: specialtiesList, isFetching: specialityFetching } =
      useSpecialtiesList({
        searchString: specialitySearch,
        pagination: specialityPagination,
        xTENANTID: getXtenantId(),
        enabled: activeReferral !== "referral-provider-list" && isFilterOpen,
      });

    const handleSpecialitySearch = (val: string) => {
      setSpecialitySearch(val);
      setSpecialityPagination((p) => ({ ...p, page: 0 }));
    };

    const hasInvalidDate = isInvalidDate(localFilters.date);

    const handleApply = () => {
      if (hasInvalidDate) return;
      setFilters(localFilters);
      setAnchorEl(null);
    };

    const handleCancel = () => {
      setLocalFilters(DEFAULT_FILTERS);
      setFilters(DEFAULT_FILTERS);
      setAnchorEl(null);
    };

    const handleExpandClick = (key: string, newExpanded: boolean) => {
      setExpanded({ ...REFERRAL_FILTER_INITIAL_EXPANDED, [key]: newExpanded });
    };
    // "Provider Name, NPI, Patient Name"
    return (
      <Flex gap={1} flex={1} alignItems={"center"} justifyContent={"end"}>
        <Box width={"18rem"}>
          <SearchInput
            searchKey={searchStringKey ?? ""}
            onSearch={(val) => setSearchStringKey?.(val)}
            placeholder={
              activeReferral === "referral-provider-list"
                ? "Search by Provider Name, NPI"
                : `Search by  Provider Name ${patientData ? "" : ", Patient Name"}`
            }
          />
        </Box>
        {activeReferral !== "referral-provider-list" && !hideFilters && (
          <GlobalFilter
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            activeFilterCount={activeFilterCount}
          >
            {REFERRAL_FILTER_KEYS.filter((it) =>
              it === "show_referral_in_out" ? showReferralInOutfilter : true
            ).map((it) => {
              return it === "date" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <Flex flexDirection={"column"} gap={1}>
                      <CustomDatePicker
                        value={
                          localFilters[it as keyof ReferralFilters] ?? null
                        }
                        onChange={(val) =>
                          setLocalFilters({
                            ...localFilters,
                            [it as keyof ReferralFilters]: val,
                          })
                        }
                        error={
                          hasInvalidDate ? INVALID_DATE_MESSAGE : undefined
                        }
                      />
                    </Flex>
                  </AccordionDetails>
                </Accordion>
              ) : it === "referral_from" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <Flex flexDirection={"column"} gap={1}>
                      <CustomSelectScroll
                        placeholder="Referral From"
                        options={(activeReferral === "referral-in"
                          ? referralProvidersList
                          : providersList
                        )?.data?.content?.map((it) => ({
                          value: it?.uuid,
                          label: it?.firstName + " " + it?.lastName,
                        }))}
                        onSearch={(_, val) => handleProviderSearch(val)}
                        loading={
                          activeReferral === "referral-in"
                            ? referralProviderFetching
                            : providerFetching
                        }
                        pagination={
                          activeReferral === "referral-in"
                            ? referralProviderPagination
                            : providerPagination
                        }
                        setPagination={
                          activeReferral === "referral-in"
                            ? setReferralProviderPagination
                            : setProviderPagination
                        }
                        totalElements={
                          (activeReferral === "referral-in"
                            ? referralProvidersList
                            : providersList
                          )?.data?.page?.totalElements
                        }
                        value={
                          Object.keys(
                            localFilters[it as keyof ReferralFilters] ?? {}
                          )?.[0] ?? undefined
                        }
                        onChange={(value, opt) => {
                          setLocalFilters({
                            ...localFilters,
                            [it as keyof ReferralFilters]:
                              opt && value
                                ? { [opt.value]: opt.label }
                                : undefined,
                          });
                        }}
                      />
                    </Flex>
                  </AccordionDetails>
                </Accordion>
              ) : it === "referral_to" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <Flex flexDirection={"column"} gap={1}>
                      <CustomSelectScroll
                        placeholder="Referral To"
                        options={(activeReferral !== "referral-in"
                          ? referralProvidersList
                          : providersList
                        )?.data?.content?.map((it) => ({
                          value: it?.uuid,
                          label: it?.firstName + " " + it?.lastName,
                        }))}
                        onSearch={(_, val) => handleProviderSearch(val)}
                        loading={
                          activeReferral !== "referral-in"
                            ? referralProviderFetching
                            : providerFetching
                        }
                        pagination={
                          activeReferral !== "referral-in"
                            ? referralProviderPagination
                            : providerPagination
                        }
                        setPagination={
                          activeReferral !== "referral-in"
                            ? setReferralProviderPagination
                            : setProviderPagination
                        }
                        totalElements={
                          (activeReferral !== "referral-in"
                            ? referralProvidersList
                            : providersList
                          )?.data?.page?.totalElements
                        }
                        value={
                          Object.keys(
                            localFilters[it as keyof ReferralFilters] ?? {}
                          )?.[0] ?? undefined
                        }
                        onChange={(value, opt) => {
                          setLocalFilters({
                            ...localFilters,
                            [it as keyof ReferralFilters]:
                              opt && value
                                ? { [opt.value]: opt.label }
                                : undefined,
                          });
                        }}
                      />
                    </Flex>
                  </AccordionDetails>
                </Accordion>
              ) : it === "speciality" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <Flex flexDirection={"column"} gap={1}>
                      <CustomSelectScroll
                        placeholder="Speciality"
                        options={specialtiesList?.data?.content?.map((it) => ({
                          value: it?.uuid,
                          label: it?.name,
                        }))}
                        onSearch={(_, val) => handleSpecialitySearch(val)}
                        loading={specialityFetching}
                        pagination={specialityPagination}
                        setPagination={setSpecialityPagination}
                        totalElements={
                          specialtiesList?.data?.page?.totalElements
                        }
                        value={
                          Object.keys(
                            localFilters[it as keyof ReferralFilters] ?? {}
                          )?.[0] ?? undefined
                        }
                        onChange={(value, opt) => {
                          setLocalFilters({
                            ...localFilters,
                            [it as keyof ReferralFilters]:
                              opt && value
                                ? { [opt.value]: opt.label }
                                : undefined,
                          });
                        }}
                      />
                    </Flex>
                  </AccordionDetails>
                </Accordion>
              ) : it === "show_referral_in_out" ? (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <Flex flexDirection={"column"} gap={1}>
                      <CustomSelect
                        placeholder="Show Referral In Out"
                        options={[
                          { value: "referral-in", label: "Referral In" },
                          { value: "referral-out", label: "Referral Out" },
                        ]}
                        value={
                          Object.keys(
                            localFilters[it as keyof ReferralFilters] ?? {}
                          )?.[0] ?? undefined
                        }
                        onChange={(value: string | null) => {
                          setLocalFilters({
                            ...localFilters,
                            [it as keyof ReferralFilters]: value,
                          });
                        }}
                      />
                    </Flex>
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Accordion
                  key={it}
                  variant="outlined"
                  expanded={expanded[it]}
                  onChange={(_, newExpanded) =>
                    handleExpandClick(it, newExpanded)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Text>{replaceUnderScoreAddSpace(it)}</Text>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 1 }}>
                    <Flex flexDirection={"column"} gap={1}>
                      <Input
                        textFieldProps={{
                          type: "text",
                          name: it,
                          value:
                            localFilters[it as keyof ReferralFilters] ?? "",
                          onChange: (e) =>
                            setLocalFilters({
                              ...localFilters,
                              [it as keyof ReferralFilters]: e.target.value,
                            }),
                          placeholder: `Enter ${replaceUnderScoreAddSpace(it)}`,
                        }}
                      />
                    </Flex>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            <Box p={2} minWidth={220}>
              <Flex width={"100%"} justifyContent={"end"}>
                <Button
                  variant="text"
                  sx={{ color: "gray" }}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  variant="text"
                  onClick={handleApply}
                  disabled={hasInvalidDate}
                >
                  Apply
                </Button>
              </Flex>
            </Box>
          </GlobalFilter>
        )}
        {/* {activeReferral === "referral-provider-list" && (
          <StyledButtonV2
            label="Add Referral Provider"
            variant="contained"
            onClick={() => newReferringProvider.open()}
          />
        )} */}
        {newReferringProvider.opened && (
          <NewReferringProvider
            open={newReferringProvider.opened}
            onClose={newReferringProvider.close}
            referralInOut={"Receiving"}
          />
        )}

        {referralDisclosureHost.opened && (
          <UpsertReferral
            prefillData={prefillReferralOutData}
            referringOutFromEncounter={referringOutFromEncounter}
            title={`${
              activeReferral === "referral-out"
                ? "Add Referral Out"
                : "Add Referral In"
            }`}
            disclosureHost={referralDisclosureHost}
          />
        )}

        {activeReferral !== "referral-provider-list" && (
          <StyledButtonV2
            label="Refer Patient"
            variant="contained"
            onClick={() => referralDisclosureHost.open()}
          />
        )}
      </Flex>
    );
  }
);
